'use client'

import { AlertCircle, ArrowLeft, Mail } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'next/navigation'
import { Link, useRouter } from '@/i18n/navigation'
import { api } from '@/lib/api'
import { BackButton } from '@/components/back-button'

export const runtime = 'edge'

export default function AdminPasswordResetRequestPage() {
  const [identifier, setIdentifier] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [resetToken, setResetToken] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const params = useParams()
  const locale = (params?.locale as string) || 'en'

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setSuccessMessage(null)
    setResetToken(null)

    if (!identifier.trim()) {
      setError('Please enter your username or email address')
      return
    }

    try {
      setIsSubmitting(true)
      const response = await api.auth.requestAdminPasswordReset(
        identifier.trim()
      )

      const payload = response.data
      setSuccessMessage(
        payload?.message ||
          'If the account exists, password reset instructions have been sent.'
      )
      if (payload?.resetToken) {
        setResetToken(payload.resetToken)
      }
    } catch (submissionError: any) {
      setError(
        submissionError?.message ||
          'We could not process your request. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleProceedToReset = () => {
    if (!resetToken) return
    router.push(`/admin/reset-password?token=${resetToken}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center">
            <Mail className="h-6 w-6 text-white" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-gray-900">
            Reset Admin Password
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Enter your username or email to receive reset instructions.
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-lg rounded-lg text-black">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-700">
                {successMessage}
              </div>
            )}

            <div>
              <label
                htmlFor="identifier"
                className="block text-sm font-medium text-gray-700"
              >
                Username or Email
              </label>
              <div className="mt-1">
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  value={identifier}
                  onChange={event => setIdentifier(event.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="your.username or you@example.com"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !identifier.trim()}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending reset link…' : 'Send reset link'}
              </button>
            </div>
          </form>

          {resetToken && (
            <div className="mt-6 space-y-3 bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
              <p className="font-medium">
                Development token (copy and keep it safe):
              </p>
              <pre className="bg-white border border-blue-200 rounded-md p-2 text-xs overflow-x-auto">
                {resetToken}
              </pre>
              <button
                type="button"
                onClick={handleProceedToReset}
                className="w-full justify-center inline-flex items-center px-3 py-2 border border-blue-600 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Continue to password reset
              </button>
            </div>
          )}

          <div className="mt-6">
            <BackButton href="/admin" label="Back to login" />
          </div>
        </div>
      </div>
    </div>
  )
}
